import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { popupToast } from '@components/Notifications/popupToast'
import { dismissToast, StandardToast } from '@components/Notifications/StandardToast'
import { Connection as SolanaConnection, VersionedTransaction } from '@solana/web3.js'
import { type SwapContext } from '@towns/userops'
import { base64ToUint8Array } from '@towns-protocol/sdk-crypto'
import { bin_toHexString, bin_toString } from '@towns-protocol/utils'
import { Signer } from 'ethers'
import {
    BlockchainTransactionType,
    generateApproveAmountCallData,
    NULL_ADDRESS,
    queryClient,
    SpaceAddressFromSpaceId,
    TransactionContext,
    TransactionStatus,
    useBaseWrites,
    useTownsClient,
    useTownsContext,
} from 'use-towns-client'
import { env } from 'utils'
import type { Address, Hex } from 'viem'

import { ETH_ADDRESS } from '../utils'

import { createSendTokenTransferDataEVM } from './hooks/evm-trading-utils'
import { createSendTokenTransferDataSolana } from './hooks/solana-utils'
import { useSolanaWallet } from './hooks/useSolanaWallet'
import { useTradingWallet } from './hooks/useTradingWallet'

type TokenInfo = {
    name: string
    symbol: string
    address: string
    amount: bigint
    decimals: number
}
export type SolanaTransactionRequest = {
    id: string
    type: 'solana'
    transactionData: string
    token: TokenInfo
    signature?: string
    status: TransactionStatus
    isBuy: boolean
    threadInfo: { channelId: string; messageId: string } | undefined
}

export type EvmTransactionRequest = {
    id: string
    type: 'evm'
    transaction: {
        toAddress: Address
        callData: Hex
        fromAmount: string
        fromTokenAddress: Address
        toTokenAddress: Address
        value: string
    }
    walletAddress: Address
    token: TokenInfo
    approvalAddress: Address
    status: TransactionStatus
    isBuy: boolean
    threadInfo: { channelId: string; messageId: string } | undefined
    estimate: {
        fromAmount: string
        toAmount: string
        minToAmount: string
    }
} & SwapContext

export type TokenTransferRollupEvent = {
    address: Uint8Array
    amount: bigint
    isBuy: boolean
    chainId: string
    userId: string
    createdAtEpochMs: bigint
    messageId: string
}

export const isSolanaTransactionRequest = (
    request: EvmTransactionRequest | SolanaTransactionRequest | undefined,
): request is SolanaTransactionRequest => {
    return request?.type === 'solana'
}

export const isEvmTransactionRequest = (
    request: EvmTransactionRequest | SolanaTransactionRequest | undefined,
): request is EvmTransactionRequest => {
    return request?.type === 'evm'
}

type SendOptions = {
    skipPendingToast?: boolean
}

const TradingContext = createContext<{
    sendSolanaTransaction: (
        transaction: SolanaTransactionRequest,
        options?: SendOptions,
    ) => void | Promise<void>
    sendEvmTransaction: (
        transaction: EvmTransactionRequest,
        signer: Signer,
        options: SendOptions,
    ) => void | Promise<void>
    pendingEvmTransaction: EvmTransactionRequest | undefined
    pendingSolanaTransaction: SolanaTransactionRequest | undefined
    tokenTransferRollups: { [key: string]: TokenTransferRollupEvent[] }
}>({
    sendSolanaTransaction: () => {},
    sendEvmTransaction: () => {},
    pendingEvmTransaction: undefined,
    pendingSolanaTransaction: undefined,
    tokenTransferRollups: {},
})

export const useTradingContext = () => {
    return useContext(TradingContext)
}

export const TradingContextProvider = ({ children }: { children: React.ReactNode }) => {
    const { solanaWallet } = useSolanaWallet()
    const townsClient = useTownsClient()
    const { updateTokenBalanceAfterSwap } = useTradingWallet()
    const [pendingSolanaTransaction, setPendingSolanaTransaction] = React.useState<
        (SolanaTransactionRequest & { skipPendingToast?: boolean }) | undefined
    >(undefined)

    const [pendingEvmTransaction, setPendingEvmTransaction] = React.useState<
        EvmTransactionRequest & { skipPendingToast?: boolean }
    >()

    const { baseTransactor, userOpsInstance } = useBaseWrites()

    const connection = useMemo(() => {
        if (!env.VITE_SOLANA_MAINNET_RPC_URL) {
            console.error('VITE_SOLANA_MAINNET_RPC_URL is not set')
            return undefined
        }
        return new SolanaConnection(env.VITE_SOLANA_MAINNET_RPC_URL)
    }, [])

    const { tokenTransferRollups } = useTokenTransferRollups()

    const sendSolanaTransaction = useCallback(
        async (transaction: SolanaTransactionRequest, options?: SendOptions) => {
            if (!solanaWallet) {
                console.error('No Solana wallet')
                return
            }

            if (!connection) {
                console.error('No Solana connection')
                return
            }

            if (pendingSolanaTransaction) {
                console.error('No Solana connection')
                return
            }

            console.info('Sending Solana transaction:', transaction)
            setPendingSolanaTransaction({
                ...transaction,
                skipPendingToast: options?.skipPendingToast,
            })

            try {
                const versionedTransaction = VersionedTransaction.deserialize(
                    base64ToUint8Array(transaction.transactionData),
                )
                const signedTx = await solanaWallet.signTransaction(versionedTransaction)
                const rawTransaction = signedTx.serialize()
                const signature = await connection.sendRawTransaction(rawTransaction, {
                    skipPreflight: true,
                    maxRetries: 5,
                })

                transaction.signature = signature
                setPendingSolanaTransaction({
                    ...transaction,
                    skipPendingToast: options?.skipPendingToast,
                })

                console.info('Solana Transaction sent:', signature)

                for (let i = 0; i < 30; i++) {
                    const txResult = await connection.getTransaction(signature, {
                        maxSupportedTransactionVersion: 0,
                    })
                    if (txResult) {
                        if (txResult.meta?.err) {
                            console.error('Solana Transaction failed:', txResult.meta?.err)
                            transaction.status = TransactionStatus.Failed
                            popupToast(({ toast }) => (
                                <StandardToast.Error
                                    message="Transaction failed"
                                    // eslint-disable-next-line @typescript-eslint/no-base-to-string
                                    subMessage={txResult.meta?.err?.toString()}
                                    toast={toast}
                                />
                            ))
                        } else if (txResult.blockTime) {
                            if (txResult.meta && transaction.threadInfo) {
                                // send token transfer event, but don't fail the entire chain
                                // of events if it fails
                                try {
                                    const transferData = createSendTokenTransferDataSolana(
                                        txResult,
                                        transaction.token.address,
                                        solanaWallet.address,
                                        transaction.threadInfo.channelId,
                                        transaction.threadInfo.messageId,
                                        transaction.isBuy,
                                    )
                                    if (transferData) {
                                        await townsClient.sendTokenTransfer(
                                            1151111081099710,
                                            transferData.receipt,
                                            transferData.event,
                                        )
                                    } else {
                                        console.error('Failed to create transfer data')
                                    }
                                } catch (error) {
                                    console.error('Error sending token transfer:', error)
                                }
                            }

                            console.log('Solana Transaction confirmed:', txResult)
                            transaction.status = TransactionStatus.Success
                            popupToast(
                                ({ toast }) => (
                                    <StandardToast.Success
                                        message="Transaction confirmed"
                                        toast={toast}
                                    />
                                ),
                                { duration: 2_000 },
                            )
                            queryClient.invalidateQueries({
                                predicate: (query) => query.queryKey[0] === 'walletContents',
                            })
                        }
                    }

                    if (transaction.status !== TransactionStatus.Pending) {
                        // we got the result, break the loop
                        break
                    }
                    console.info('Waiting for Solana transaction confirmation...')
                    await new Promise((resolve) => setTimeout(resolve, 1000))
                }

                setPendingSolanaTransaction(undefined)
            } catch (error) {
                console.error('Error sending Solana transaction:', error)
            }
            setPendingSolanaTransaction(undefined)
        },
        [
            solanaWallet,
            connection,
            setPendingSolanaTransaction,
            pendingSolanaTransaction,
            townsClient,
        ],
    )

    const sendEvmTransaction = useCallback(
        async (request: EvmTransactionRequest, signer: Signer, options?: SendOptions) => {
            setPendingEvmTransaction({
                ...request,
                status: TransactionStatus.Pending,
                skipPendingToast: options?.skipPendingToast,
            })

            function showErrorToast(subMessage: string) {
                popupToast(({ toast }) => (
                    <StandardToast.Error
                        message="Transaction failed"
                        subMessage={subMessage}
                        toast={toast}
                    />
                ))
            }

            try {
                let transactionContext: TransactionContext<void> | undefined

                // LiFi is using 0x0000....0 but the contract expects 0xEee...Eee
                const remappedTokenInAddress =
                    request.transaction.fromTokenAddress === NULL_ADDRESS
                        ? ETH_ADDRESS
                        : request.transaction.fromTokenAddress
                const remappedTokenOutAddress =
                    request.transaction.toTokenAddress === NULL_ADDRESS
                        ? ETH_ADDRESS
                        : request.transaction.toTokenAddress

                const contractAddress =
                    request.spaceId !== undefined
                        ? SpaceAddressFromSpaceId(request.spaceId)
                        : request.swapRouterAddress

                // IF this is a token transfer, we need to approve the token first
                // by bundling an approve call with the actual transaction call.
                // call approve(spender, amount)
                const approval =
                    request.transaction.fromTokenAddress !== NULL_ADDRESS
                        ? {
                              callData: generateApproveAmountCallData(
                                  contractAddress,
                                  request.transaction.fromAmount,
                              ) as Hex,
                              toAddress: request.transaction.fromTokenAddress,
                          }
                        : undefined

                // Determine poster based on trading context
                const poster =
                    request.spaceId !== undefined
                        ? SpaceAddressFromSpaceId(request.spaceId)
                        : NULL_ADDRESS

                transactionContext = await baseTransactor.createTransaction({
                    type: BlockchainTransactionType.Trade,
                    signer,
                    debugName: 'sendExecuteSwapOp',
                    aaExecute: () => {
                        return userOpsInstance?.sendExecuteSwapOp({
                            exactInputParams: {
                                tokenIn: remappedTokenInAddress,
                                tokenOut: remappedTokenOutAddress,
                                amountIn: BigInt(request.transaction.fromAmount),
                                minAmountOut: BigInt(request.estimate.minToAmount),
                                recipient: request.walletAddress,
                            },
                            routerParams: {
                                router: request.transaction.toAddress,
                                approveTarget: request.approvalAddress,
                                swapData: request.transaction.callData,
                            },
                            poster: poster as Address,
                            value: BigInt(request.transaction.value),
                            signer,
                            approval,
                            ...request,
                        })
                    },
                    eoaExecute: () => {
                        throw new Error('not supported for EOAs')
                    },
                    errorDecoder: (error) =>
                        baseTransactor.tryDecodeError(error, 'Failed to execute swap') ??
                        new Error('Failed to execute swap'),
                })

                if (!transactionContext) {
                    showErrorToast('Failed to send transaction')
                    return
                }

                transactionContext = await baseTransactor.waitForBlockchainTransaction(
                    transactionContext,
                    undefined,
                    2,
                )

                request.status = transactionContext?.status ?? TransactionStatus.Failed
                setPendingEvmTransaction(undefined)

                if (transactionContext?.status === TransactionStatus.Success) {
                    popupToast(
                        ({ toast }) => (
                            <StandardToast.Success message="Transaction confirmed" toast={toast} />
                        ),
                        { duration: 2_000 },
                    )

                    /**
                     * Update token balances using the specific block number from the transaction receipt.
                     *
                     * CRITICAL: We must use the exact block number where the swap transaction was mined
                     * to ensure consistent balance data across RPC nodes. Without this:
                     * - Swap transaction goes to Node A at block N
                     * - Balance queries hit Node B which may still be at block N-1
                     * - User sees stale balances until Node B catches up
                     *
                     * By pinning balance queries to the transaction's block number, we guarantee
                     * that all RPC nodes return the same post-swap balance data.
                     */
                    const blockNumber = transactionContext.receipt?.blockNumber
                    await updateTokenBalanceAfterSwap(
                        request.transaction.fromTokenAddress as Address,
                        request.transaction.toTokenAddress as Address,
                        blockNumber,
                    )

                    if (request.threadInfo && transactionContext.receipt) {
                        try {
                            const transferData = createSendTokenTransferDataEVM(
                                transactionContext.receipt,
                                request.walletAddress,
                                request.token.address,
                                request.threadInfo.channelId,
                                request.threadInfo.messageId,
                                request.isBuy,
                            )
                            if (transferData) {
                                await townsClient.sendTokenTransfer(
                                    8453,
                                    transferData.receipt,
                                    transferData.event,
                                )
                            } else {
                                console.error('Failed to create transfer data')
                            }
                        } catch (error) {
                            console.error('Error sending token transfer:', error)
                        }
                    }
                } else {
                    showErrorToast('Transaction failed')
                    return
                }
            } catch (error) {
                showErrorToast('Failed to send transaction')
                request.status = TransactionStatus.Failed
                setPendingEvmTransaction(undefined)
                console.error('Error sending EVM transaction:', error)
            }
        },
        [baseTransactor, townsClient, updateTokenBalanceAfterSwap, userOpsInstance],
    )

    const pendingTransactionData = pendingSolanaTransaction?.transactionData

    useEffect(() => {
        if (
            !pendingTransactionData ||
            pendingSolanaTransaction?.skipPendingToast ||
            pendingSolanaTransaction?.status === TransactionStatus.Success
        ) {
            return
        }
        const toastId = popupToast(
            ({ toast }) => (
                <StandardToast.Pending
                    message="Pending transaction"
                    subMessage="Please wait for the transaction to complete"
                    toast={toast}
                />
            ),
            { duration: Infinity },
        )
        return () => {
            if (toastId) {
                dismissToast(toastId)
            }
        }
    }, [pendingTransactionData, pendingSolanaTransaction])

    const pendingEvmTransactionData = pendingEvmTransaction?.transaction
    useEffect(() => {
        if (
            !pendingEvmTransaction ||
            pendingEvmTransaction.skipPendingToast ||
            pendingEvmTransaction?.status === TransactionStatus.Success
        ) {
            return
        }
        const toastId = popupToast(
            ({ toast }) => (
                <StandardToast.Pending
                    message="Pending transaction"
                    subMessage="Please wait for the transaction to complete"
                    toast={toast}
                />
            ),
            { duration: Infinity },
        )
        return () => {
            if (toastId) {
                dismissToast(toastId)
            }
        }
    }, [pendingEvmTransactionData, pendingEvmTransaction])

    return (
        <TradingContext.Provider
            value={{
                pendingSolanaTransaction,
                pendingEvmTransaction,
                sendSolanaTransaction,
                sendEvmTransaction,
                tokenTransferRollups,
            }}
        >
            {children}
        </TradingContext.Provider>
    )
}

const useTokenTransferRollups = () => {
    const { casablancaClient } = useTownsContext()

    // map of trades: token address -> transfers
    const [rollup, setRollup] = useState<{
        [key: string]: {
            channelId: string
            address: Uint8Array
            amount: bigint
            isBuy: boolean
            chainId: string
            userId: string
            createdAtEpochMs: bigint
            messageId: string
        }[]
    }>({})

    useEffect(() => {
        if (!casablancaClient) {
            return
        }

        const onStreamTokenTransfer = (channelId: string, transfer: TokenTransferRollupEvent) => {
            // solana does not use hex strings for addresses
            const address =
                transfer.chainId === 'solana-mainnet'
                    ? bin_toString(transfer.address)
                    : '0x' + bin_toHexString(transfer.address)
            setRollup((prevRollup) => {
                const newRollup = { ...prevRollup }
                if (!newRollup[address]) {
                    newRollup[address] = []
                }
                newRollup[address] = [...newRollup[address], { ...transfer, channelId }]
                return newRollup
            })
        }

        const onStreamInitialized = (streamId: string) => {
            const stream = casablancaClient.streams.get(streamId)
            if (!stream) {
                return
            }
            for (const tokenTransfer of stream.view.membershipContent.tokenTransfers) {
                onStreamTokenTransfer(stream.view.streamId, tokenTransfer)
            }
        }

        casablancaClient.on('streamTokenTransfer', onStreamTokenTransfer)
        casablancaClient.on('streamInitialized', onStreamInitialized)
        return () => {
            casablancaClient.off('streamTokenTransfer', onStreamTokenTransfer)
            casablancaClient.off('streamInitialized', onStreamInitialized)
        }
    }, [casablancaClient, setRollup])
    return { tokenTransferRollups: rollup }
}
